import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, InputNumber } from 'antd';
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ulid } from 'ulid';

import { InputSwitch } from '../Input';
import { PLUS } from '../icons';
import { effectiveElementNames, indexSavedElements } from '../../utils/conversions';
import { formatCount } from '../../utils/counts';
import { DEFAULT_MAX_ENTRIES, GROUP_PANEL_OPTIONS, MAX_COLUMNS, MAX_ENTRIES_CEILING, columnsOf, maxEntriesOf } from '../../config/repeatingGroups';

/**
 * The card editor's panel for a REPEATING GROUP element (KPR-679; epic
 * KPE-027): how many entries an item may hold, whether it offers section
 * headings, and the group's COLUMNS.
 *
 * A column is an element in every respect the API cares about (keepr-api
 * utils/repeatingGroups.js, KPR-675), so each one is edited by the SAME
 * element editor as a card's own element, hung from the column's own path
 * (`renderColumn`, handed in by CardDefElement, which owns the editor — this
 * file never imports it, so the two do not import each other). Nothing here
 * decides which options a column may carry: that is the editor's column mode,
 * from config/repeatingGroups.js.
 *
 * What the panel owns:
 *
 *   - the idea, in one sentence with the example the gallery tile gives;
 *   - Minimum entries / Maximum entries, with the default and the ceiling
 *     said where they are typed. The ceiling is not enforced here: a number
 *     past it reaches the API and comes back as the API's own sentence
 *     (`entries_over_ceiling`), so the one place the platform's limit lives
 *     stays the API;
 *   - Section headings (`options.sections`);
 *   - the ordered column list: Add column, a drag handle, and Move up / Move
 *     down in each column's ⋯ (a drag is not a keyboard's way, nor a phone's);
 *   - the group-level refusals, where the setting they are about is edited,
 *     and the column-level ones handed to the column they name.
 *
 * A refusal about a column arrives as `{ element: <group>, column: <name> }`
 * (the API) or `{ groupIndex, columnIndex }` (the editor's own gates). The
 * column's editor is given it TRANSLATED — `element` := the column's name —
 * so every inline display the element editor already has (a type error, an
 * off-type option, a conversion, a measure) works on a column unchanged.
 */

const GROUP_PANEL_CODES = ['entries_over_ceiling', 'repeating_group_placement', 'repeating_groups_off'];

const newColumn = () => ({
	name: '',
	dataType: '',
	label: { singular: '' },
	options: { choices: [] },
});

function SortableColumn({ id, children }) {
	const { attributes, listeners, setActivatorNodeRef, setNodeRef, transform, transition, isDragging } = useSortable({ id });
	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.6 : 1,
		position: isDragging ? 'relative' : undefined,
		zIndex: isDragging ? 999 : undefined,
	};
	return (
		<div ref={setNodeRef} style={style}>
			{children({ attributes, listeners, setActivatorNodeRef })}
		</div>
	);
}

export default function RepeatingGroupOptions({
	element,
	index,
	basePath = null,
	elementName = '',
	savedElement = null,
	onChange,
	saveError = null,
	disabled = false,
	renderColumn,
}) {
	const base = basePath || `elements[${index}]`;
	const options = element?.options || {};
	const columns = columnsOf(element);
	const optionsPath = `${base}.options`;

	// ---- Which refusal is about this group, and which about a column --------
	const mine = saveError && (
		(typeof saveError.groupIndex === 'number' && saveError.groupIndex === index)
		|| (saveError.element && saveError.element === elementName)
	) ? saveError : null;
	const columnNames = useMemo(() => effectiveElementNames(columns), [columns]);
	const savedColumns = useMemo(() => indexSavedElements(columnsOf(savedElement)), [savedElement]);

	const refusalFor = (position) => {
		if (!mine) return null;
		const named = mine.column && mine.column === columnNames[position];
		const indexed = typeof mine.columnIndex === 'number' && mine.columnIndex === position;
		// `invalid_element_type` from the API carries the column's index.
		const apiIndexed = !!mine.column && typeof mine.index === 'number' && mine.index === position && mine.code === 'invalid_element_type';
		if (!named && !indexed && !apiIndexed) return null;
		return { ...mine, element: columnNames[position], index: position, column: undefined, columnIndex: undefined, groupIndex: undefined };
	};
	const groupRefusal = mine && !mine.column && typeof mine.columnIndex !== 'number'
		&& (GROUP_PANEL_OPTIONS.includes(mine.option) || GROUP_PANEL_CODES.includes(mine.code))
		? mine
		: null;

	// ---- Row identities and which columns are open -------------------------
	// Kept in step DURING RENDER (not in an effect) for the reason the element
	// rows are (CardDefElements): an effect fills them a paint late, keying the
	// first frame `undefined` and then remounting every row.
	const [uiIds, setUiIds] = useState(() => columns.map(() => ulid()));
	const [openKeys, setOpenKeys] = useState([]);
	let ids = uiIds;
	if (ids.length !== columns.length) {
		ids = [...ids];
		while (ids.length < columns.length) ids.push(ulid());
		while (ids.length > columns.length) ids.pop();
		setUiIds(ids);
	}
	const toggleOpen = key => setOpenKeys(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]));

	// A refused save points at ONE column, whose sentence lives in its row: open
	// it, once per refusal (the group's row is opened by CardDefElements).
	const revealedRef = useRef(null);
	useEffect(() => {
		if (!mine) { revealedRef.current = null; return; }
		if (revealedRef.current === mine) return;
		const position = columns.findIndex((_, i) => (
			(mine.column && mine.column === columnNames[i])
			|| (typeof mine.columnIndex === 'number' && mine.columnIndex === i)
			|| (mine.column && typeof mine.index === 'number' && mine.index === i && mine.code === 'invalid_element_type')
		));
		const key = ids[position];
		if (position < 0 || !key) return;
		revealedRef.current = mine;
		setOpenKeys(prev => (prev.includes(key) ? prev : [...prev, key]));
	}, [mine, columns, columnNames, ids]);

	// ---- Edits --------------------------------------------------------------
	const atColumnCap = columns.length >= MAX_COLUMNS;
	const addColumn = () => {
		if (disabled || atColumnCap) return;
		const key = ulid();
		setUiIds(prev => [...prev, key]);
		setOpenKeys(prev => [...prev, key]);
		onChange(`${optionsPath}.columns`, [...columns, newColumn()]);
	};
	const removeColumn = (position) => {
		setUiIds(prev => prev.filter((_, i) => i !== position));
		onChange(`${optionsPath}.columns`, columns.filter((_, i) => i !== position));
	};
	const moveColumn = (from, to) => {
		if (from === to || to < 0 || to >= columns.length) return;
		setUiIds(prev => arrayMove(prev, from, to));
		onChange(`${optionsPath}.columns`, arrayMove(columns, from, to));
	};

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
	);
	const handleDragEnd = ({ active, over }) => {
		if (!over || active.id === over.id) return;
		moveColumn(ids.indexOf(active.id), ids.indexOf(over.id));
	};

	// ---- Entries ------------------------------------------------------------
	const setCount = key => (value) => {
		const clean = Number.isFinite(value) ? value : undefined;
		onChange(`${optionsPath}.${key}`, clean);
	};
	const max = options.maxEntries;
	const min = options.minEntries;
	const overCeiling = Number.isFinite(max) && max > MAX_ENTRIES_CEILING;
	const minOverMax = Number.isFinite(min) && min > (Number.isFinite(max) ? max : maxEntriesOf(element));
	const minId = `${optionsPath}.minEntries`;
	const maxId = `${optionsPath}.maxEntries`;

	return (
		<div className='repeating-group' data-testid='repeating-group-options'>
			<div className='micro-label card-def-element__section'>Repeating group</div>
			<p className='form-panel__hint repeating-group__idea'>
				A group of elements that repeat: each entry has the columns below. A Recipe card&rsquo;s
				Ingredients group, for example, has the columns Name, Amount and Note, and every recipe holds as
				many ingredients as it needs.
			</p>

			<div className='micro-label card-def-element__section'>Entries</div>
			<div className='card-def-element__grid'>
				<div className='card-def-element__field'>
					<label className='d-block' htmlFor={minId}>Minimum entries</label>
					<InputNumber
						id={minId}
						size='large'
						className='repeating-group__count'
						min={0}
						precision={0}
						value={Number.isFinite(min) ? min : null}
						placeholder='0'
						disabled={disabled}
						status={minOverMax ? 'warning' : undefined}
						onChange={setCount('minEntries')}
					/>
					<div className='form-panel__hint'>Leave blank for none. An item needs at least this many.</div>
				</div>
				<div className='card-def-element__field'>
					<label className='d-block' htmlFor={maxId}>Maximum entries</label>
					<InputNumber
						id={maxId}
						size='large'
						className='repeating-group__count'
						min={1}
						precision={0}
						value={Number.isFinite(max) ? max : null}
						placeholder={String(DEFAULT_MAX_ENTRIES)}
						disabled={disabled}
						status={overCeiling ? 'warning' : undefined}
						onChange={setCount('maxEntries')}
					/>
					<div className='form-panel__hint' data-testid='max-entries-hint'>
						Leave blank for {DEFAULT_MAX_ENTRIES}. Up to {MAX_ENTRIES_CEILING}.
					</div>
				</div>
			</div>
			{minOverMax ? (
				<div className='form-panel__hint repeating-group__warning' role='status'>
					The minimum is more than the maximum, so no item could be saved.
				</div>
			) : null}
			{groupRefusal ? (
				<Alert
					className='card-def-element__option-error repeating-group__refusal'
					type='error'
					showIcon
					data-testid='repeating-group-refusal'
					message={groupRefusal.message || 'The repeating group was refused.'}
				/>
			) : null}
			<div className='card-def-element__toggles'>
				<InputSwitch
					elementName={`${optionsPath}.sections`}
					elementLabel='Section headings'
					description='Entries can also be headings that split a long list into sections. A heading has a title and no other columns.'
					value={options.sections === true}
					onChange={(name, checked) => onChange(name, checked ? true : undefined)}
					disabled={disabled}
					first
				/>
			</div>

			<div className='form-panel__kicker-row repeating-group__columns-head'>
				<div className='micro-label card-def-element__section repeating-group__columns-title'>
					Columns
					{columns.length ? <span className='form-panel__count'>{columns.length}</span> : null}
				</div>
				<Button icon={<PLUS />} onClick={addColumn} disabled={disabled || atColumnCap}>Add column</Button>
			</div>
			{atColumnCap ? (
				<div className='form-panel__hint' data-testid='column-cap-note'>
					A group holds at most {MAX_COLUMNS} columns.
				</div>
			) : null}
			{columns.length ? null : (
				<div className='form-panel__hint repeating-group__empty' data-testid='no-columns-note'>
					No columns yet. Add one for each thing an entry records — a name, an amount, a note.
				</div>
			)}

			<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
				<SortableContext items={ids} strategy={verticalListSortingStrategy}>
					<div className='repeating-group__columns' data-testid='repeating-group-columns'>
						{columns.map((column, position) => (
							<SortableColumn key={ids[position]} id={ids[position]}>
								{dragHandleProps => renderColumn({
									key: ids[position],
									column,
									columnIndex: position,
									path: `${optionsPath}.columns[${position}]`,
									elementName: columnNames[position],
									savedColumn: savedColumns[columnNames[position]] || null,
									siblings: columns,
									error: refusalFor(position),
									dragHandleProps,
									isOpen: openKeys.includes(ids[position]),
									onToggleOpen: () => toggleOpen(ids[position]),
									onRemove: () => removeColumn(position),
									onMove: delta => moveColumn(position, position + delta),
									canMoveUp: position > 0,
									canMoveDown: position < columns.length - 1,
								})}
							</SortableColumn>
						))}
					</div>
				</SortableContext>
			</DndContext>

			{columns.length >= 3 && !atColumnCap ? (
				<div className='card-def-elements__foot'>
					<Button icon={<PLUS />} onClick={addColumn} disabled={disabled}>Add column</Button>
				</div>
			) : null}
			{columns.length ? (
				<div className='form-panel__hint' data-testid='columns-count'>
					{formatCount(columns.length, 'column')}. Removing a column hides what entries hold under it; it does not delete it.
				</div>
			) : null}
			{columns.length ? (
				// Column rules (KPR-688): where a column's access is set, in the
				// door's own words (the column's ⋯ menu, the card's Overview).
				<div className='form-panel__hint' data-testid='columns-access-note'>
					Who can see or change a column follows the group. To narrow it for one column, open that column&rsquo;s
					{' '}&#8943; menu and choose Who can see or change it&hellip; A column never gives anyone more than the group does.
				</div>
			) : null}
		</div>
	);
}
